import { forwardRef, useEffect, useRef, useState, useMemo, useImperativeHandle } from 'react';
import Quill from 'quill';
import { Scope } from 'parchment';
import store from 'app/store';
// import QuillResizeImage from 'quill-resize-image';
import QuillImageResizer from '@mouseoverllc/quill-image-resizer';
import QuillTableBetter from "quill-table-better/dist/quill-table-better";
// Types
import NotificationStatuses from 'app/types/NotificationStatuses';
// Redux
import { useAppDispatch } from 'app/hooks/useStore';
// Async
import { AppUiNotificationsActions } from 'app/store/AppUINotifications/AppUINotifications.slice';
// Mui
import { Theme, Paper, FormControl, FormHelperText, SxProps, Box } from '@mui/material';
// Components
import { Loader } from 'app/components/Utilities';
// i18next
import { useTranslation } from 'react-i18next';
import { cleanupPastedFormatting } from './ClipboardOptions';
// Utilities
import { normalizeQuillContent } from 'app/utilities/Utilities';
import { sanitizeHTML } from './utils/html-sanitizer';

import '@mouseoverllc/quill-image-resizer/dist/style.css';
import "quill-table-better/dist/quill-table-better.css";

import './FontFamilyFormat';
import './FontSizeFormat';
import './TabStopFormat';
import './PageBreakBlot';
import './SpanElements';
import './LinkVariableFormat';
import './CommentBlot';

import './CCTheme'
import './modules/PasteSanitizeModule';
import { CCQuillOptions } from './CCTheme';
import pasteImage from './utils/image-paste';
import uploadImage from './utils/image-upload';

(window as any).Quill = Quill;

// Preserve whiteSpace
class PreserveWhiteSpace {
  constructor(private quill: any, private options: {}) {
    quill.container.style.whiteSpace = "pre-line";
  }
}
Quill.register('modules/preserveWhiteSpace', PreserveWhiteSpace);
Quill.register({"modules/table-better": QuillTableBetter}, true);

// Image resizer
Quill.register('modules/imageResizer', QuillImageResizer);

type Props = {
  id: string;
  label: string;
  value: string;
  required?: boolean;
  disabled? :boolean;
  error?: boolean;
  helperText?: string;
  onChange: (value:string) => void;
  onFocus?: () => void;

  formControlSx?: SxProps;
  paperSx?: SxProps;

  isTableVisible?: boolean;

  onSetInitialContent?: (content:string) => void;

  tooltipOptions?: any;
  
  sanitizeInitialContent?: boolean; // Sanitize HTML on initial content load (default: true)
  sanitizePasteContent?: boolean; // Sanitize HTML on paste (default: true)
}

const imageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
const maxImageSize = 5 * 1024 * 1024; // 5MB in bytes

const QuillEditor = forwardRef<any, Props>(({
  // Props
  id, label, value, required, disabled = false, error, helperText, onChange, onFocus, formControlSx = {}, paperSx = {}, onSetInitialContent, tooltipOptions = {}, sanitizeInitialContent = true, sanitizePasteContent = true
}, ref) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const editorRef = useRef<HTMLDivElement>(null);
  const quillInstance = useRef<Quill | null>(null);

  useImperativeHandle(ref, () => ({
    getQuill: () => quillInstance.current
  }), []);

  const [ loading, setLoading ] = useState(false);
  const [ rendered, setRendered ] = useState(false);

  const renderImageLoading = useMemo(() => {
    if ( !loading ) return null;
    return (
      <Box sx={{
        position: 'absolute',
        top: 0, left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'rgba(255,255,255,0.75)',
        zIndex: 1
      }}><Loader /></Box>
    );
  }, [loading]);

  useEffect(() => {
    // if ( !editorRef.current || quillInstance.current ) return;

    const quill = new Quill(editorRef.current as HTMLElement, {
      theme: "ссtheme",
      placeholder: disabled ? '' : `${label}${required ? ' *' : ''}`,
      readOnly: disabled,
      tooltipOptions: tooltipOptions,
      store,
      modules: {
        history: {
          delay: 1000,
          maxStack: 100,
          userOnly: true, // only record user actions
        },
        toolbar: {
          container: `#toolbar-${id}`,
          handlers: {
            variable: variableHandler,
            linkVariable: linkVariableHandler,
            image: function(){
              const input = document.createElement('input') as HTMLInputElement;
              input.setAttribute('type', 'file');
              input.setAttribute('accept', imageTypes.join(','));
              input.click();
              input.onchange = async () => {
                if ( !input.files ) return;
                const file = input.files[0];
                if ( file.size > maxImageSize ){
                  dispatch(AppUiNotificationsActions.addSnackbar({
                    message: t('notifications.quillEditor.sizeError'),
                    options: { variant: NotificationStatuses.Error }
                  }))
                  return;
                }
                await uploadImage({
                  file,
                  startLoading: () => setLoading(true),
                  endLoading: () => setLoading(false),
                  callback: (imageUrl: string) => {
                    pasteImage(quill, imageUrl);
                  }
                });
              }
            },
            pagebreak: pageBreakHandler,
            tabstop: tabStopHandler,
            clean: function(this: any) {
              const range = this.quill.getSelection();
              if (range == null) return;
              if (range.length === 0) {
                const formats = this.quill.getFormat();
                Object.keys(formats).forEach((name) => {
                  // Clean functionality in existing apps only clean inline formats
                  if (this.quill.scroll.query(name, Scope.INLINE) != null) {
                    this.quill.format(name, false, Quill.sources.USER);
                  }
                });
              } else {
                const delta = this.quill.getContents(range.index, range.length);
                let idx = range.index;
                delta.ops.forEach((op: any) => {
                  const insert = op.insert;
                  const len = typeof insert === 'string' ? insert.length : 1;
                  const isImage = typeof insert === 'object' && insert != null && (insert.image != null);
                  // If this segment is not an image embed, remove formats from it.
                  if (!isImage) {
                    this.quill.removeFormat(idx, len, Quill.sources.USER);
                  }
                  idx += len;
                });
              }
            },
          }
        },
        imageResizer: {},
        table: false,
        'table-better': {
          menus: ['wrap', 'column', 'row', 'delete'],
          toolbarTable: true
        },
        pasteSanitize: {
          dispatch,
          startLoading: () => setLoading(true),
          endLoading: () => setLoading(false),
          removeColor: true,
          removeBackground: true,
          sanitizeHTML: sanitizePasteContent
        },
        keyboard: {
          bindings: QuillTableBetter.keyboardBindings,
        },
        clipboard: {
          matchers: [
            [Node.ELEMENT_NODE, (node: any, delta: any) => {
              delta.ops = delta.ops.map((op: any) => cleanupPastedFormatting(node, op));
              return delta;
            }]
          ]
        }
      },
    } as CCQuillOptions);

    quillInstance.current = quill;

    setRendered(true);

    const handleTextChange = () => {
      const innerHTML = quill.root.innerHTML;
      onChange(innerHTML === '<p><br></p>' ? '' : innerHTML);
    }

    const handleFocus = () => {
      if ( typeof onFocus === 'undefined' ) return;
      onFocus();
    }

    quill.on("text-change", handleTextChange);
    quill.root.addEventListener("focus", handleFocus);

    return () => {
      quill.off("text-change", handleTextChange);
      quill.root.removeEventListener("focus", handleFocus);
    }
    // eslint-disable-next-line
  }, [uploadImage, dispatch, t]);

  useEffect(() => {
    if ( !rendered ) return;
    const quill = quillInstance.current;
    if ( !quill ) return;
    // Set initial content
    const normalizedHtml = normalizeQuillContent(value);
    // Sanitize HTML before setting content (if enabled)
    const finalHtml = sanitizeInitialContent ? sanitizeHTML(normalizedHtml) : normalizedHtml;
    const delta = quill.clipboard.convert({ html: finalHtml });
    const [range] = quill.selection.getRange();
    quill.updateContents(delta, Quill.sources.SILENT);
    quill.setSelection(
      delta.length() - (range?.length || 0),
      Quill.sources.SILENT
    );
    if ( onSetInitialContent ) onSetInitialContent(quill.root.innerHTML);
    // eslint-disable-next-line
  }, [rendered]);

  return (
    <FormControl sx={{
      position: 'relative',
      display: 'flex',
      height: '100%',
      overflow: 'hidden',
      ...formControlSx
    }}>
      {renderImageLoading}
      <Paper
        translate="no"
        ref={editorRef}
        sx={{
          borderColor: (theme:Theme) => error ? theme.palette.error.main : 'rgba(0,0,0,0.23)',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          ...paperSx
        }}
      />
      {helperText ? (
        <FormHelperText
          sx={{
            position: 'absolute',
            bottom: 0, left: 0,
            mb: 2,
            zIndex: 1
          }}
          error={error}
        >{helperText}</FormHelperText>
      ) : null}
    </FormControl>
  )
})

export default QuillEditor;

function variableHandler(this:any, value:string) {
  const quill = this.quill;
  const selection = quill.getSelection();
  if ( !selection ) return;
  quill.insertEmbed(selection.index, 'spanVariable', value);
  // quill.setSelection(selection.index + value.length + 1);
}
function linkVariableHandler(this:any, value:string) {
  const quill = this.quill;
  const selection = quill.getSelection();
  if ( !selection ) return;
  quill.formatText(selection.index, selection.length, 'link_variable', value);
  // quill.setSelection(selection.index + value.length + 1);
}
function pageBreakHandler(this:any) {
  const quill = this.quill;
  const selection = quill.getSelection();
  if ( !selection ) return;
  quill.insertEmbed(selection.index, 'pagebreak', true, 'user');
  quill.insertText(selection.index + 1, '\n', 'user');
  quill.setSelection(selection.index + 2);
}
function tabStopHandler(this:any, value:any) {
  const quill = this.quill;
  const selection = quill.getSelection();
  if ( !selection ) return;
  quill.formatLine(selection.index, selection.length, 'tabstop', value);
}

